import type { ArticleSuppLang } from '~/types/common/article'

/** 获取多语言文章内容 */
export function useI18nContent() {
  const { locale } = useI18n()
  const { formatOrigPath } = useI18nTools()
  const mdPath = formatOrigPath()
  const { ARTICLE_SUPP_LANGS, ARTICLE_DEFAULT_LNAG } = useConfigStatic().configSite

  /** 将 locale 的横杠替换为下划线（用于匹配 content 文件键名） */
  const normalizedLang = computed(() => useFormatContentLang(locale.value, true))
  /** 类型守卫：判断是否为支持的文章语言 */
  function isSupportedArticleLang(lang: string): lang is ArticleSuppLang {
    return (ARTICLE_SUPP_LANGS as readonly string[]).includes(lang)
  }
  /** 当前语言是否被文章内容支持 */
  const isLangSupported = computed(() => isSupportedArticleLang(normalizedLang.value))

  /** 最终用于加载文章的语言（保证一定是 ArticleSuppLang 类型） */
  const resolvedLang = computed<ArticleSuppLang>(() => {
    const candidate = normalizedLang.value
    return isSupportedArticleLang(candidate) ? candidate : ARTICLE_DEFAULT_LNAG
  })

  /**
   * 获取多语言文章内容
   * @return 单篇目标文章内容对象
   */
  const getArticle = async () => {
    return await useAsyncData(`getArticle-${mdPath.value}-${resolvedLang.value}`, () => {
      return queryCollection(`articles_${resolvedLang.value}`).path(mdPath.value).first()
    })
  }

  /**
   * 所有文章导航索引
   * @return 所有文章导航索引数组
   */
  const getArticleList = async () => {
    useDPrint('获取导航索引', resolvedLang.value)
    return await useAsyncData(`getArticleList-${resolvedLang.value}`, () =>
      queryCollectionNavigation(
        `articles_${resolvedLang.value}`,
        // ❗额外定义需要返回的数据字段
        ['description', 'tags', 'bannerImg', 'created'],
      ).order('created', 'DESC'),
    )
  }

  /**
   * 异步搜索+多语言文章内容
   * @return 搜索结果数组
   */
  const searchDbArticle = () => {
    return useSearchCollection(`articles_${resolvedLang.value}`, {
      immediate: false,
      //   ignoredTags: ['style'],
    })
  }

  /**
   * 获取新闻类文章导航索引
   * @return 新闻类文章导航索引数组，注意：无需再做解构
   */
  const getArticleListByStem = async (): Promise<any[]> => {
    const articleList = await getArticleList()
    // useDPrint('articleList.data.value', JSON.stringify(articleList.data.value))
    return (
      articleList.data.value
        // 由于外层有一层额外的stem为about的结构，这里需要额外扁平化一次，如果在根部则不用
        ?.flatMap(item => item.children)
        ?.find(item => item?.stem === 'about/news')?.children || []
    )
  }

  /**
   * 获取所有文件数据
   * @return 所有文件数据数组
   */
  const getFileList = async () => {
    return await useAsyncData(`getFileList-${resolvedLang.value}`, () =>
      queryCollectionSearchSections(`articles_${resolvedLang.value}`),
    )
  }

  /**
   * 获取上下篇文章数据
   * @returns 上下篇文章数据数组
   */
  const getSurroundDocs = async () => {
    return await useAsyncData(`getSurroundDocs-${resolvedLang.value}`, () => {
      useDPrint('获取相邻文章数据')
      return queryCollectionItemSurroundings(`articles_${resolvedLang.value}`, mdPath.value)
    })
  }

  return {
    getArticle,
    getArticleList,
    getArticleListByStem,
    getFileList,
    getSurroundDocs,
    searchDbArticle,
    isLangSupported,
    resolvedLang,
  }
}
